import { Component } from "react";

/**
 * App-wide safety net. If any descendant component throws during render or in
 * a lifecycle method, we show a friendly Portuguese error screen with a
 * "Recarregar" button instead of a blank page. The underlying stack is still
 * logged to the browser console for diagnostics.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "" };
  }

  static getDerivedStateFromError(err) {
    const msg = err?.message || "Erro inesperado";
    // Auto-recover from browser-translation DOM races. Chrome's
    // auto-translate (and a few extensions) injects nodes into the
    // page, then React tries to reconcile the same subtree and fails
    // with one of these errors. We force-reset state and skip showing
    // the error screen — once. If it keeps happening on the same page,
    // the boundary falls through normally so the issue is visible.
    const isDomRace = /removeChild|insertBefore|appendChild|Node|is not a child/i.test(msg);
    if (isDomRace && !window.__luxstay_dom_race_seen) {
      window.__luxstay_dom_race_seen = true;
      // Schedule reload after current task — gives React a chance to
      // unmount cleanly first.
      setTimeout(() => { try { window.location.reload(); } catch (_) { /* ignore */ } }, 50);
      return { hasError: false, message: "" };
    }
    return { hasError: true, message: msg };
  }

  componentDidCatch(err, info) {
    // eslint-disable-next-line no-console
    console.error("[LuxStay ErrorBoundary]", err, info?.componentStack);
  }

  handleReload = () => {
    this.setState({ hasError: false, message: "" });
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-screen bg-[#041f1b] text-[#fdfbf7] flex items-center justify-center p-6"
           data-testid="error-boundary">
        <div className="lux-card p-10 max-w-lg w-full text-center space-y-6">
          <div className="eyebrow text-[#d4af37]">Algo correu mal</div>
          <h1 className="font-display text-3xl md:text-4xl">Ocorreu um erro inesperado.</h1>
          <p className="text-sm text-[#a0b2af] leading-relaxed">
            A aplicação encontrou um problema ao renderizar esta página.
            Os seus dados estão seguros — pode recarregar para tentar novamente
            ou voltar ao início.
          </p>
          {this.state.message && (
            <div className="text-xs text-[#a0b2af] opacity-70 border border-[#1a4640] rounded-sm p-3 text-left font-mono">
              {this.state.message}
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={this.handleReload}
                    className="btn-gold px-5 py-3 text-sm"
                    data-testid="error-boundary-reload">
              Recarregar página
            </button>
            <button onClick={this.handleGoHome}
                    className="px-5 py-3 text-sm border border-[#1a4640] rounded-sm text-[#a0b2af] hover:border-[#d4af37] hover:text-[#d4af37] transition-colors"
                    data-testid="error-boundary-home">
              Voltar ao início
            </button>
          </div>
        </div>
      </div>
    );
  }
}
